import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getSettings, getTeam, patchSettings, importWorkspace, createTeamMember, updateTeamMember, deleteTeamMember, getMcpInfo, generateMcpToken, saveMcpServiceToken, getProbe42Access, grantProbe42Access, revokeProbe42Access, getProbe42Usage } from '../lib/api.js';
import { Card, PageHeader, Pill } from '../components/ui.js';

const ROLES = ['admin', 'partner', 'analyst', 'viewer'];
const inputCls = 'w-full px-3 py-2 border border-line rounded-lg text-[13px]';

export function Settings() {
  const qc = useQueryClient();
  const { data: sData } = useQuery({ queryKey: ['settings'], queryFn: getSettings, retry: false });
  const { data: tData } = useQuery({ queryKey: ['team'], queryFn: getTeam, retry: false });
  const s = sData?.settings;
  const team = tData?.team ?? [];

  const [form, setForm] = useState<{ fundName?: string; icThreshold?: string; intakeNumber?: string }>({});
  const val = (k: 'fundName' | 'intakeNumber') => form[k] ?? (s?.[k] ?? '');
  const thrVal = form.icThreshold ?? String(s?.icThreshold ?? 70);
  const dirty = Object.keys(form).length > 0;
  const [importMsg, setImportMsg] = useState('');

  const save = useMutation({
    mutationFn: () => patchSettings({ fundName: val('fundName'), intakeNumber: val('intakeNumber'), icThreshold: Number(thrVal) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings'] }); qc.invalidateQueries({ queryKey: ['deals'] }); setForm({}); },
  });

  // ── Team & roles ──────────────────────────────────────────────────────────
  const [nm, setNm] = useState({ name: '', email: '', role: 'analyst' });
  const [teamMsg, setTeamMsg] = useState('');
  const refreshTeam = () => qc.invalidateQueries({ queryKey: ['team'] });
  const addMember = useMutation({
    mutationFn: () => createTeamMember({ name: nm.name.trim(), email: nm.email.trim() || undefined, role: nm.role }),
    onSuccess: () => { setNm({ name: '', email: '', role: 'analyst' }); setTeamMsg(''); refreshTeam(); },
    onError: (e) => setTeamMsg(String((e as Error).message)),
  });
  const editMember = useMutation({
    mutationFn: (v: { id: string; patch: Partial<{ name: string; email: string; role: string; active: boolean }> }) => updateTeamMember(v.id, v.patch),
    onSuccess: () => { setTeamMsg(''); refreshTeam(); },
    onError: (e) => setTeamMsg(String((e as Error).message)),
  });
  const removeMember = useMutation({
    mutationFn: (id: string) => deleteTeamMember(id),
    onSuccess: () => { setTeamMsg(''); refreshTeam(); },
    onError: (e) => setTeamMsg(String((e as Error).message)),
  });

  async function exportJson() {
    const data = await api<unknown>('/export');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    a.download = 'krit_workspace_export.json';
    a.click();
  }
  async function importJson(file: File) {
    setImportMsg('Importing…');
    try {
      const data = JSON.parse(await file.text());
      const res = await importWorkspace(data);
      setImportMsg(`Imported ${res.upserted} deals.`);
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['settings'] });
    } catch (e) {
      setImportMsg(`Import failed: ${String((e as Error).message)}`);
    }
  }

  return (
    <div className="max-w-[820px]">
      <PageHeader eyebrow="Tools" title="Settings" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Fund">
          <div className="space-y-3">
            <div><label className="block text-[11px] font-semibold text-muted mb-1">Fund name</label><input className={inputCls} value={val('fundName')} onChange={(e) => setForm((p) => ({ ...p, fundName: e.target.value }))} /></div>
            <div><label className="block text-[11px] font-semibold text-muted mb-1">IC threshold</label><input type="number" className="w-32 px-3 py-2 border border-line rounded-lg text-[13px] mono" value={thrVal} onChange={(e) => setForm((p) => ({ ...p, icThreshold: e.target.value }))} /></div>
            <div><label className="block text-[11px] font-semibold text-muted mb-1">WhatsApp intake number</label><input className={inputCls} value={val('intakeNumber')} onChange={(e) => setForm((p) => ({ ...p, intakeNumber: e.target.value }))} /></div>
            <button disabled={!dirty || save.isPending} onClick={() => save.mutate()} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white disabled:opacity-50">{save.isPending ? 'Saving…' : 'Save fund settings'}</button>
          </div>
        </Card>

        <Card title="Data" sub="Full workspace backup / restore">
          <p className="text-[12.5px] text-muted mb-3">Export a JSON backup (deals, team, settings, notes, updates), or import to upsert deals by id.</p>
          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={exportJson} className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas">↓ Export JSON</button>
            <label className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line cursor-pointer hover:bg-canvas">↑ Import JSON
              <input type="file" accept="application/json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importJson(f); e.target.value = ''; }} />
            </label>
          </div>
          {importMsg && <p className="text-[11px] text-muted mt-2">{importMsg}</p>}
          <p className="text-[11px] text-muted-2 mt-2">Scheduled backups arrive in a later slice.</p>
        </Card>
      </div>

      <McpSettings />

      <Probe42UsageSettings />

      <Probe42AccessSettings />

      <Card title="Team &amp; roles" sub={`${team.length} members · the email is what Cloudflare Access matches on for login`} className="mt-4" bodyClass="p-0">
        {/* Add member */}
        <div className="flex flex-wrap items-end gap-2 px-5 py-3 border-b border-line-2 bg-canvas">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10.5px] font-semibold text-muted mb-1">Name</label>
            <input className={inputCls} placeholder="Full name" value={nm.name} onChange={(e) => setNm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[10.5px] font-semibold text-muted mb-1">Email (login)</label>
            <input className={inputCls} placeholder="name@company.com" value={nm.email} onChange={(e) => setNm((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-muted mb-1">Role</label>
            <select className="px-3 py-2 border border-line rounded-lg text-[13px] capitalize" value={nm.role} onChange={(e) => setNm((p) => ({ ...p, role: e.target.value }))}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button
            disabled={!nm.name.trim() || addMember.isPending}
            onClick={() => addMember.mutate()}
            className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white disabled:opacity-50"
          >{addMember.isPending ? 'Adding…' : '+ Add member'}</button>
        </div>

        <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12.5px] min-w-[520px]">
          <thead><tr>{['Name', 'Email', 'Role', 'Status', ''].map((h) => <th key={h} className="text-left text-muted font-bold text-[10.5px] uppercase px-5 py-2.5 border-b border-line-2">{h}</th>)}</tr></thead>
          <tbody>
            {team.map((m) => (
              <tr key={m.id} className={`hover:bg-canvas ${m.active ? '' : 'opacity-55'}`}>
                <td className="px-5 py-2.5 border-b border-line-2 font-semibold text-navy">{m.name}</td>
                <td className="px-5 py-2.5 border-b border-line-2 text-muted">{m.email ?? <span className="text-red">no email — can't log in</span>}</td>
                <td className="px-5 py-2.5 border-b border-line-2">
                  <select
                    className="px-2 py-1 border border-line rounded-md text-[12px] capitalize bg-white"
                    value={m.role}
                    onChange={(e) => editMember.mutate({ id: m.id, patch: { role: e.target.value } })}
                  >{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select>
                </td>
                <td className="px-5 py-2.5 border-b border-line-2">
                  <button
                    onClick={() => editMember.mutate({ id: m.id, patch: { active: !m.active } })}
                    className="text-[11px] font-semibold"
                  ><Pill tone={m.active ? 'green' : 'gray'}>{m.active ? '✓ active' : 'inactive'}</Pill></button>
                </td>
                <td className="px-5 py-2.5 border-b border-line-2 text-right">
                  <button
                    onClick={() => { if (confirm(`Remove ${m.name}?`)) removeMember.mutate(m.id); }}
                    className="text-[11px] font-semibold text-red hover:underline"
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
      {teamMsg && <p className="text-[11.5px] text-red mt-2">{teamMsg}</p>}
      <p className="text-[11px] text-muted-2 mt-2">Add someone here (with their login email) to grant access — no database edits needed. Members without an email can be POC/owners but can't sign in. Toggle <em>active</em> to revoke access without deleting history.</p>
    </div>
  );
}

/**
 * Registry Intelligence (Probe42) usage — it's a CHARGEABLE API, so this is a plain, fund-wide log of
 * every pull/refresh: who, when, which deal, and whether it actually cost money (production) or was
 * a free sandbox-era call. Reuses the same audit_log entries the per-deal usage table already reads
 * (decision.md D5/D12) — this is just the rollup across every deal in one place.
 */
function Probe42UsageSettings() {
  const { data } = useQuery({ queryKey: ['probe42-usage'], queryFn: getProbe42Usage, retry: false });
  const summary = data?.summary;
  const calls = data?.calls ?? [];

  return (
    <Card title="Registry Intelligence usage" sub="Chargeable Probe42 calls across the whole portfolio" className="mt-4">
      {!summary ? (
        <p className="text-[12px] text-muted-2">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2">
              <div className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">Chargeable calls</div>
              <div className="text-[20px] font-bold text-navy">{summary.productionCalls}</div>
            </div>
            <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2">
              <div className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">This month</div>
              <div className="text-[20px] font-bold text-navy">{summary.productionThisMonth}</div>
            </div>
            <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2">
              <div className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">Deals charged</div>
              <div className="text-[20px] font-bold text-navy">{summary.uniqueDealsCharged}</div>
            </div>
            <div className="rounded-lg border border-line-2 bg-canvas px-3 py-2">
              <div className="text-[10.5px] font-semibold text-muted uppercase tracking-wide">Free sandbox calls</div>
              <div className="text-[20px] font-bold text-muted">{summary.sandboxCalls}{summary.unknownCalls > 0 ? ` (+${summary.unknownCalls} unlabeled)` : ''}</div>
            </div>
          </div>
          {calls.length === 0 ? (
            <p className="text-[12px] text-muted-2">No Probe42 calls yet.</p>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-[12.5px]">
                <thead><tr className="text-muted text-left border-b border-line-2 sticky top-0 bg-white">
                  <th className="py-1.5 pr-3 font-semibold">When</th><th className="py-1.5 pr-3 font-semibold">Deal</th><th className="py-1.5 pr-3 font-semibold">Action</th><th className="py-1.5 pr-3 font-semibold">By</th><th className="py-1.5 font-semibold">Cost</th>
                </tr></thead>
                <tbody>
                  {calls.map((r) => (
                    <tr key={r.id} className="border-b border-line-2/60">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-muted">{new Date(r.ts).toLocaleString()}</td>
                      <td className="py-1.5 pr-3"><span className="font-semibold text-navy">{r.short ?? r.dealId ?? '—'}</span> <span className="text-muted-2">{r.company ?? ''}</span></td>
                      <td className="py-1.5 pr-3 text-muted">{r.action === 'probe42-fetch' ? 'Comprehensive pull' : 'Refresh'}</td>
                      <td className="py-1.5 pr-3 text-muted">{r.actor ?? '—'}</td>
                      <td className="py-1.5">
                        {r.sandbox === false ? <span className="text-[11px] font-semibold text-red">$ chargeable</span>
                          : r.sandbox === true ? <span className="text-[11px] text-muted-2">free (sandbox)</span>
                          : <span className="text-[11px] text-muted-2">unknown</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Probe42 access — admin-only by default (decision.md D3); this is the "approve whom to add" flow
 * on top of that (D15): anyone who hits Request Access on the Probe42 tab shows up here once, and an
 * admin can grant or later revoke them individually, without opening the role up to their whole tier.
 */
function Probe42AccessSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['probe42-access'], queryFn: getProbe42Access, retry: false });
  const pending = data?.pending ?? [];
  const granted = data?.granted ?? [];
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['probe42-access'] });

  const grant = useMutation({
    mutationFn: (memberId: string) => grantProbe42Access(memberId),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => { setMsg(''); refresh(); },
    onError: (e) => setMsg(String((e as Error).message)),
    onSettled: () => setBusyId(null),
  });
  const revoke = useMutation({
    mutationFn: (memberId: string) => revokeProbe42Access(memberId),
    onMutate: (id) => setBusyId(id),
    onSuccess: () => { setMsg(''); refresh(); },
    onError: (e) => setMsg(String((e as Error).message)),
    onSettled: () => setBusyId(null),
  });

  return (
    <Card title="Registry Intelligence access" sub="Who can trigger the chargeable Registry Intelligence pull, beyond admins" className="mt-4">
      <p className="text-[12.5px] text-muted mb-3">Admins always have access. Everyone else sees <b>Request Access</b> on a deal's Registry Intelligence tab — approve someone here to grant just them, without changing their role.</p>
      {msg && <p className="text-[11.5px] text-red mb-2">{msg}</p>}

      <div className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">Pending requests {pending.length > 0 && `(${pending.length})`}</div>
      {pending.length === 0 ? (
        <p className="text-[12px] text-muted-2 mb-4">No open requests.</p>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-muted text-left border-b border-line-2">
              <th className="py-1.5 pr-3 font-semibold">Who</th><th className="py-1.5 pr-3 font-semibold">Role</th><th className="py-1.5 pr-3 font-semibold">Asked from</th><th className="py-1.5 pr-3 font-semibold">When</th><th className="py-1.5 font-semibold"></th>
            </tr></thead>
            <tbody>
              {pending.map((r) => (
                <tr key={r.actorId} className="border-b border-line-2/60">
                  <td className="py-1.5 pr-3"><span className="font-semibold text-navy">{r.name ?? '—'}</span> <span className="text-muted-2">{r.email}</span></td>
                  <td className="py-1.5 pr-3 capitalize text-muted">{r.role}</td>
                  <td className="py-1.5 pr-3 text-muted">{r.company ?? '—'}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-muted">{new Date(r.ts).toLocaleString()}</td>
                  <td className="py-1.5 text-right">
                    <button disabled={!r.actorId || busyId === r.actorId} onClick={() => r.actorId && grant.mutate(r.actorId)}
                      className="px-2.5 py-1 rounded-md text-[11.5px] font-semibold bg-teal text-white disabled:opacity-50">{busyId === r.actorId ? 'Approving…' : '✓ Approve'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] font-semibold text-muted uppercase tracking-wide mb-1.5">Granted</div>
      {granted.length === 0 ? (
        <p className="text-[12px] text-muted-2">No one's been granted individually yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-muted text-left border-b border-line-2">
              <th className="py-1.5 pr-3 font-semibold">Who</th><th className="py-1.5 pr-3 font-semibold">Granted by</th><th className="py-1.5 pr-3 font-semibold">When</th><th className="py-1.5 font-semibold"></th>
            </tr></thead>
            <tbody>
              {granted.map((g) => (
                <tr key={g.memberId} className="border-b border-line-2/60">
                  <td className="py-1.5 pr-3"><span className="font-semibold text-navy">{g.name ?? '—'}</span> <span className="text-muted-2">{g.email}</span></td>
                  <td className="py-1.5 pr-3 text-muted">{g.grantedBy ?? '—'}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-muted">{new Date(g.grantedAt).toLocaleString()}</td>
                  <td className="py-1.5 text-right">
                    <button disabled={busyId === g.memberId} onClick={() => { if (confirm(`Revoke Registry Intelligence access for ${g.name}?`)) revoke.mutate(g.memberId); }}
                      className="text-[11.5px] font-semibold text-red hover:underline disabled:opacity-50">{busyId === g.memberId ? 'Revoking…' : 'Revoke'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** MCP connector — generate/rotate the token, store the Cloudflare service token, copy the full config. */
function McpSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['mcp-info'], queryFn: getMcpInfo, retry: false });
  const [copied, setCopied] = useState<'' | 'token' | 'config'>('');
  const [genErr, setGenErr] = useState('');
  const [cfId, setCfId] = useState<string | null>(null);
  const [cfSecret, setCfSecret] = useState<string | null>(null);
  // Local edits fall back to the saved values from the server.
  const clientId = cfId ?? data?.cfClientId ?? '';
  const clientSecret = cfSecret ?? data?.cfClientSecret ?? '';

  const gen = useMutation({
    mutationFn: generateMcpToken,
    onSuccess: () => { setGenErr(''); qc.invalidateQueries({ queryKey: ['mcp-info'] }); },
    onError: (e) => setGenErr(String((e as Error).message)),
  });
  const saveCf = useMutation({
    mutationFn: () => saveMcpServiceToken(clientId, clientSecret),
    onSuccess: () => { setCfId(null); setCfSecret(null); qc.invalidateQueries({ queryKey: ['mcp-info'] }); },
  });

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://krit-deal-engine.pages.dev';
  const url = `${origin}/api/mcp`;
  const token = data?.token ?? null;
  const headers: Record<string, string> = { Authorization: `Bearer ${token ?? '<MCP_TOKEN>'}` };
  if (clientId || clientSecret) { headers['CF-Access-Client-Id'] = clientId || '<CLIENT_ID>'; headers['CF-Access-Client-Secret'] = clientSecret || '<CLIENT_SECRET>'; }
  const snippet = JSON.stringify({ mcpServers: { 'krit-deal-engine': { type: 'http', url, headers } } }, null, 2);
  const copy = (what: 'token' | 'config', text: string) => { void navigator.clipboard?.writeText(text).then(() => { setCopied(what); setTimeout(() => setCopied(''), 1400); }).catch(() => {}); };
  const inCls = 'w-full px-2.5 py-1.5 border border-line rounded-lg text-[11.5px] mono';

  return (
    <Card title="MCP connector" sub="Query the fund's deals from a Claude / Cloud session — read-only (list_deals · get_deal · ask)" className="mt-4">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <Pill tone={data?.enabled ? 'green' : 'amber'}>{data?.enabled ? 'Enabled' : 'Not configured'}</Pill>
        <button disabled={gen.isPending} onClick={() => gen.mutate()}
          className="px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-navy text-white hover:bg-navy-2 disabled:opacity-50">
          {gen.isPending ? 'Generating…' : token ? '↻ Regenerate token' : '🔑 Generate token'}
        </button>
      </div>
      {genErr && <p className="text-[11.5px] text-red mb-2">{genErr}</p>}
      <div className="text-[12px] text-muted mb-2">Endpoint: <span className="mono text-navy break-all">{url}</span></div>

      {/* MCP token — app-managed, rotate anytime here */}
      {token ? (
        <div className="mb-3">
          <div className="text-[11px] font-semibold text-muted mb-1">MCP token <span className="font-normal text-muted-2">· rotate anytime with ↻ Regenerate; teammates then re-copy the config</span></div>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-[11.5px] bg-canvas border border-line-2 rounded-lg px-3 py-1.5 mono text-navy">{token}</code>
            <button onClick={() => copy('token', token)} className="px-2 py-1.5 rounded-md text-[10.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas flex-none">{copied === 'token' ? '✓' : 'Copy'}</button>
          </div>
        </div>
      ) : (
        <p className="text-[11.5px] text-muted mb-3">No token yet — click Generate to enable the connector.</p>
      )}

      {/* Cloudflare Access service token — infra, set once by an admin, stays the same */}
      <div className="rounded-lg border border-line-2 bg-canvas p-3 mb-3">
        <div className="text-[11px] font-semibold text-navy mb-0.5">Cloudflare Access service token <span className="font-normal text-muted-2">· set once (infra) so external clients pass Access — no per-user rotation</span></div>
        <p className="text-[10.5px] text-muted-2 mb-2">Create it in Cloudflare (Access → Service Auth), add a Service-Auth policy to this app, then paste the pair here to fill the config below. Stays the same for everyone; only an admin rotates it in Cloudflare if it leaks.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input className={inCls} placeholder="CF-Access-Client-Id (…​.access)" value={clientId} onChange={(e) => setCfId(e.target.value)} />
          <input className={inCls} placeholder="CF-Access-Client-Secret" value={clientSecret} onChange={(e) => setCfSecret(e.target.value)} />
        </div>
        <button disabled={saveCf.isPending} onClick={() => saveCf.mutate()} className="mt-2 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">{saveCf.isPending ? 'Saving…' : saveCf.isSuccess ? '✓ Saved' : 'Save service token'}</button>
      </div>

      <div className="text-[11px] font-semibold text-muted mb-1">Claude MCP config <span className="font-normal text-muted-2">· paste into your client, restart</span></div>
      <div className="relative">
        <pre className="text-[11px] bg-canvas border border-line-2 rounded-lg p-3 pr-16 overflow-x-auto mono text-navy">{snippet}</pre>
        <button onClick={() => copy('config', snippet)} className="absolute top-2 right-2 px-2 py-1 rounded-md text-[10.5px] font-semibold bg-white text-navy border border-line hover:bg-canvas">{copied === 'config' ? '✓ Copied' : 'Copy'}</button>
      </div>
      <p className="text-[11px] text-muted-2 mt-2">Everyone uses this same config. Rotating the <b>MCP token</b> (above) is the app-level rotation — do it here and re-share. The <b>service token</b> is Cloudflare-side infra and stays put. Full setup in <span className="mono">docs/mcp-connector.md</span>.</p>
    </Card>
  );
}
